import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { formatDate, showErrorToast, showInfoToast, showSuccessToast, getDayPeriodFromDate, getLastUpdatedFromDate } from "../../app/utils/helpers";
import { Localize } from "../../app/lang/langs";

const initialState = {
  list: [],
  myList: [],
  leaveModalType: false,
  selectedLeaveData: false,
  selectedLeaveFormData: {},
  formattedList: [],
  myFormattedList: [],
  myLeavesLoaded: false,
  adjustmentModalOpen: false,
  adjustments: [],
  adjustmentsLoaded: false,
  // Balances of the collab open in the adjustments modal, as returned by the API
  // after each get/add/remove (the backend recomputes them immediately).
  adjustmentsBalance: null,
  // PDF justificatifs for the leave currently open in the form modal. Keyed by nothing —
  // only one leave is open at a time, and the list is refetched on open, so a stale list
  // from a previously-viewed leave can never be shown.
  attachments: [],
  attachmentsLoading: false,
  attachmentUploading: false
}

/**
 * add/removeLeaveAdjustment answer with the collab's balance recomputed on the spot;
 * keep it so the modal shows the new solde without another round trip.
 */
const handleAdjustmentResponse = (state, payload) => {
  const data = payload && payload.responseData
  if (!data) {
    showErrorToast(Localize("ERROR"))
    return
  }
  if (data.status !== 1) {
    showErrorToast(data.message)
    return
  }
  if (data.balance) state.adjustmentsBalance = data.balance
  if (data.recomputed === false) showInfoToast(Localize("ADJUSTMENT_NOT_RECOMPUTED"))
  else showSuccessToast(data.message)
}

const createPlanningObject = (planningItem, useFormattedDates = false) => ({
  value: planningItem.id,
  label: planningItem["task"],
  id: planningItem["id"],
  id_silae: planningItem["id_silae"],
  nom: planningItem["nom"] ? planningItem["nom"].trim() : '',
  prenom: planningItem["prenom"] ? planningItem["prenom"].trim() : '',
  email: planningItem["email"],
  company: planningItem["company"],
  // 'interne' / 'externe', off the collab's contract — shown as a tag in the Congés table.
  type_collab: planningItem["type_collab"],
  id_sample_shift: planningItem["id_sample_shift"],
  id_sample_leave: planningItem["id_sample_leave"],
  id_collab: planningItem["id_collab"],
  id_groupe: planningItem['id_groupe'],
  clientId: planningItem["id_client"],
  id_team: planningItem["id_team"],
  type: planningItem["type"],
  // 0 when the leave type accepts no attachment at all, so the table's flag column can
  // treat "none" and "not applicable" the same way — both render nothing.
  attachmentsCount: Number(planningItem["attachments_count"] || 0),
  hasAttachment: Number(planningItem["attachments_count"] || 0) > 0,
  validatedBy: planningItem["validated_by"],
  task: planningItem["task"],
  description: planningItem["description"],
  from: useFormattedDates ? formatDate(new Date(planningItem["from"]), "DD/MM/YYYY") + " " + getDayPeriodFromDate(planningItem["from"]) : new Date(planningItem['from']).getTime(),
  to: useFormattedDates ? formatDate(new Date(planningItem["to"]), "DD/MM/YYYY") + " " + getDayPeriodFromDate(planningItem["to"]) : new Date(planningItem['to']).getTime(),
  fromDate: planningItem["from"] ? new Date(planningItem["from"]).getTime() : null,
  toDate: planningItem["to"] ? new Date(planningItem["to"]).getTime() : null,
  duration: `${planningItem["duration"]} j`,
  status: planningItem["status"],
  createdAt: getLastUpdatedFromDate(planningItem['created_at']),
  // Raw, for sorting and filtering — `createdAt` above is already a display string.
  createdAtTime: planningItem['created_at'] ? new Date(planningItem['created_at']).getTime() : null,
  // The team the leave was taken in, and the collab's team today (see getPlanning).
  teamName: planningItem["team_name"] || null,
  currentTeamName: planningItem["current_team_name"] || null,
  updatedAt: new Date(planningItem['updated_at']).getTime(),
  validatedAt: planningItem['validated_at'] ? new Date(planningItem['validated_at']).getTime() : null,
  createdBy: planningItem["createdBy"],
  updatedBy: planningItem["updatedBy"],
  validatedBy: planningItem["validatedBy"]
})

export const leavesSlice = createSlice({
  name: "leaves",
  initialState,
  reducers: {
    showLeaveModal: (state, action) => {
      state.selectedLeaveData = action.payload.data
      state.leaveModalType = action.payload.type
      if (action.payload.data)
        state.selectedLeaveFormData = action.payload.data;
    },
    hideLeaveModal: (state, action) => {
      state.leaveModalType = false
      state.selectedLeaveData = false
    },
    setSelectedLeaveData: (state, action) => {
      state.selectedLeaveData = action.payload;
    },
    showAdjustmentModal: (state, action) => {
      state.adjustmentModalOpen = true
    },
    hideAdjustmentModal: (state, action) => {
      state.adjustmentModalOpen = false
      state.adjustments = []
      state.adjustmentsLoaded = false
      state.adjustmentsBalance = null
    },
    setLeaveStatus: (state, action) => {
      let leaveId = action.payload.id
      for (const key in state.list) {
        if (state.list[key].id == leaveId) {
          state.list[key].status = action.payload.status
          if (action.payload.status == "VALIDATED") {
            state.list[key].validatedAt = getLastUpdatedFromDate(new Date().getTime())
          }
        }
      }
    },
    // Called when the leave form closes. Without it the next leave opened would briefly
    // render the previous one's documents before its own fetch resolves.
    clearLeaveAttachments: (state) => {
      state.attachments = []
      state.attachmentsLoading = false
      state.attachmentUploading = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLeaveAttachments.pending, (state) => {
      state.attachmentsLoading = true
    })
    builder.addCase(getLeaveAttachments.fulfilled, (state, action) => {
      state.attachmentsLoading = false
      const data = action.payload && action.payload.responseData
      // Empty on failure rather than left as-is: showing the previous leave's documents
      // because this request 403'd would be the worst possible failure mode here.
      state.attachments = data && data.status == 1 && Array.isArray(data.list) ? data.list : []
    })
    builder.addCase(getLeaveAttachments.rejected, (state) => {
      state.attachmentsLoading = false
      state.attachments = []
    })
    builder.addCase(uploadLeaveAttachment.pending, (state) => {
      state.attachmentUploading = true
    })
    builder.addCase(uploadLeaveAttachment.fulfilled, (state) => {
      state.attachmentUploading = false
    })
    builder.addCase(uploadLeaveAttachment.rejected, (state) => {
      state.attachmentUploading = false
    })
    builder.addCase(getLeaves.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData["planning"]) {
        const { planning } = action.payload.responseData;

        let planningList = Object.keys(planning).map(key => createPlanningObject(planning[key], true));
        let planningList2 = Object.keys(planning).map(key => createPlanningObject(planning[key]));

        state.list = planningList;
        state.formattedList = planningList2;
        
      }
    }).addCase(requestLeave.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        if (action.payload.responseData.status === 1)
          showSuccessToast(action.payload.responseData.message)
        else if (showErrorToast(action.payload.responseData.message)) {
          showErrorToast(action.payload.responseData.message)
        }
      }
    })
      .addCase(getMyLeaves.fulfilled, (state, action) => {
        state.myLeavesLoaded = true
        if (action.payload.responseData && action.payload.responseData["planning"]) {
          const { planning } = action.payload.responseData;
          let planningList = Object.keys(planning).map(key => createPlanningObject(planning[key], true));
          let planningList2 = Object.keys(planning).map(key => createPlanningObject(planning[key]));
          state.myList = planningList;
          state.myFormattedList = planningList2;
        }
      })
      .addCase(getMyLeaves.rejected, (state, action) => {
        state.myLeavesLoaded = true
      })
      .addCase(requestLeave.rejected, (state, action) => {

      })
      .addCase(submitLeave.fulfilled, (state, action) => {

      })
      .addCase(requestDeleteLeave.fulfilled, (state, action) => {

      })
      .addCase(getLeaveAdjustments.pending, (state, action) => {
        state.adjustmentsLoaded = false
      })
      .addCase(getLeaveAdjustments.fulfilled, (state, action) => {
        state.adjustmentsLoaded = true
        if (action.payload.responseData && action.payload.responseData["adjustments"]) {
          state.adjustments = action.payload.responseData["adjustments"]
          state.adjustmentsBalance = action.payload.responseData["balance"] || null
        } else {
          state.adjustments = []
        }
      })
      .addCase(getLeaveAdjustments.rejected, (state, action) => {
        state.adjustmentsLoaded = true
        state.adjustments = []
      })
      .addCase(addLeaveAdjustment.fulfilled, (state, action) => {
        handleAdjustmentResponse(state, action.payload)
      })
      .addCase(removeLeaveAdjustment.fulfilled, (state, action) => {
        handleAdjustmentResponse(state, action.payload)
      })
  },
})

export const getLeaves = createAsyncThunk(
  "planning/getLeaves",
  async (payload) => {
    try {
      let body = {
        teamsIds: payload.selectedTeamsIds,
        collabsIds: payload.selectedCollabsIds,
        type: "leave"
      }
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getPlanning,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const getMyLeaves = createAsyncThunk(
  "planning/getMyLeaves",
  async (payload) => {
    try {
      let body = {
        monthsCount: payload?.monthsCount || 6
      }
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getMyLeaves,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const requestLeave = createAsyncThunk(
  "planning/requestLeave",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.requestLeave,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const submitLeave = createAsyncThunk(
  "planning/submitLeave",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.submitLeave,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)


export const deleteLeave = createAsyncThunk(
  "planning/deleteLeave",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.deleteLeave,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const requestDeleteLeave = createAsyncThunk(
  "planning/requestDeleteLeave",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.requestDeleteLeave,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const getLeaveAttachments = createAsyncThunk(
  "leaves/getLeaveAttachments",
  async (idPlanning) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.listLeaveAttachments,
        { id_planning: idPlanning }
      )
      return response
    } catch (error) {
      return { responseError: error }
    }
  }
)

export const uploadLeaveAttachment = createAsyncThunk(
  "leaves/uploadLeaveAttachment",
  async ({ file, idPlanning }, { dispatch }) => {
    try {
      // Not through postRequest: this one is multipart, so it goes via the dedicated
      // NetworkManager helper.
      const data = await NetworkManager.getInstance().uploadLeaveAttachment(file, idPlanning)
      if (data && data.status == 1) {
        showSuccessToast(Localize('ATTACHMENT_UPLOADED'))
        // Refetched rather than pushed locally: the row the backend stored carries the
        // generated filename and the uploader's name, neither of which the client knows.
        dispatch(getLeaveAttachments(idPlanning))
        return { ok: true }
      }
      showErrorToast((data && data.error) || Localize('ATTACHMENT_UPLOAD_FAILED'))
      return { ok: false }
    } catch (error) {
      const message = error && error.response && error.response.data && error.response.data.error
      showErrorToast(message || Localize('ATTACHMENT_UPLOAD_FAILED'))
      return { ok: false }
    }
  }
)

export const deleteLeaveAttachment = createAsyncThunk(
  "leaves/deleteLeaveAttachment",
  async ({ id, idPlanning }, { dispatch }) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.deleteLeaveAttachment,
        { id }
      )
      if (response && response.responseData && response.responseData.status == 1) {
        dispatch(getLeaveAttachments(idPlanning))
      }
      return response
    } catch (error) {
      return { responseError: error }
    }
  }
)

export const getLeaveAdjustments = createAsyncThunk(
  "planning/getLeaveAdjustments",
  async (params) => {
    try {
      let response = await NetworkManager.getInstance().getRequest(
        config.apiPaths.planning.getLeaveAdjustments,
        { id_collab: params.idCollab }
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const addLeaveAdjustment = createAsyncThunk(
  "planning/addLeaveAdjustment",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.addLeaveAdjustment,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const removeLeaveAdjustment = createAsyncThunk(
  "planning/removeLeaveAdjustment",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.removeLeaveAdjustment,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const { showLeaveModal,
  hideLeaveModal,
  setSelectedLeaveData,
  setLeaveStatus,
  showAdjustmentModal,
  hideAdjustmentModal,
  clearLeaveAttachments
} = leavesSlice.actions;

export default leavesSlice.reducer;
